/* eslint-disable react-hooks/exhaustive-deps */
import { Grid, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { ColorHelper, TableHelper, TimeHelper } from "helpers";
import { usePaginationRequest } from "hooks/useRequest";
import {
  EPalletType,
  IDownloadPalletMasterDataRequest,
  IPaginateApiResponse,
  IPaginationRequest,
  IPallet,
} from "model";
import moment from "moment";
import { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { DownloadActions, PalletActions } from "redux/actions";
import { useAppSelector } from "redux/configurations/reduxHooks";
import { PalletApiService } from "services";
import { CURRENT_PAGE_SIZE, EXPORT_FILE_NAME, FIRST_PAGE } from "utils/Const";
import { I18nTranslator, translations } from "utils/i18n";
import AppTable from "../../../../components/molecules/AppTable";
import { FilterPopup, ModalAddPallet } from "./Components";

const PalletConfiguration = () => {
  const dispatch = useDispatch();
  const [currentPalletType, setCurrentPalletType] = useState<EPalletType>(
    EPalletType.ALL
  );
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const downloading = useAppSelector(
    (state) => state.AppConfigReducer.downloading
  );
  const action = useAppSelector((state) => state.PalletReducer.action);
  const [addPalletModalVisible, setAddPalletModalVisible] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [request, setRequest] = useState<IPaginationRequest>({
    pageNumber: FIRST_PAGE,
    pageSize: CURRENT_PAGE_SIZE,
  });

  const { loading, result } = usePaginationRequest<
    IPaginationRequest,
    IPallet[]
  >(
    request,
    PalletApiService.getAllPallets,
    [refresh],
    {
      refresh: refresh,
    },
    () => {
      setRefresh(false);
    }
  );

  useEffect(() => {
    if (action.includes(PalletActions.createPallets.successName)) {
      setRefresh(true);
    }
  }, [action]);

  const handlePageChange = (pageNumber: number) => {
    setRequest({ ...request, pageNumber: pageNumber });
  };
  const handleSearchTextChange = (searchText: string) => {
    setRequest({
      ...request,
      pageNumber: FIRST_PAGE,
      searchText: searchText,
    });
  };
  const _handleTypeChange = (type: EPalletType) => {
    setCurrentPalletType(type);

    setRequest({
      ...request,
      pageNumber: FIRST_PAGE,
      type: type,
    });
  };
  const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const _handlePageSizeChange = (pageSize: number) => {
    setRequest({ ...request, pageSize: pageSize });
  };

  const _downloadPalletMasterData = () => {
    const params: IDownloadPalletMasterDataRequest = {
      ...request,
      pageSize: (result as IPaginateApiResponse<IPallet[]>).totalRecords,
      pageNumber: FIRST_PAGE,
      type: currentPalletType,
      fileName: EXPORT_FILE_NAME.palletMasterData,
    };
    dispatch(DownloadActions.downloadPalletMasterData.request(params));
  };

  const COLUMNS: GridColDef[] = [
    TableHelper.renderSTT(),
    TableHelper.createCell({
      field: "palletCode",
      headerName: I18nTranslator(translations.masterData.palletId),
      size: "small",
      align: "left",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "palletType",
      headerName: I18nTranslator(translations.masterData.type),
      size: "small",
      align: "left",
      renderCell: ({ row }) => {
        return (
          <Typography
            style={{
              backgroundColor: ColorHelper.getPalletTypeColor(
                row.type as EPalletType
              ),
              padding: "3px 16px",
              borderRadius: "3px",
              color: "white",
              fontWeight: "bold",
            }}
          >
            {row.palletType}
          </Typography>
        );
      },
    }),
    TableHelper.createCell({
      field: "zoneName",
      headerName: I18nTranslator(translations.masterData.zone),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "lineName",
      headerName: I18nTranslator(translations.masterData.line),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "locationName",
      headerName: I18nTranslator(translations.masterData.location),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "cartonQuantity",
      headerName: I18nTranslator(translations.masterData.goods),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "strStatus",
      headerName: I18nTranslator(translations.masterData.status),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "isDeleted",
      headerName: I18nTranslator(translations.masterData.usageStatus),
      size: "small",
      align: "left",
      renderCell: ({ value }) => {
        return <Typography>{value ? "Đã huỷ" : "Đang sử dụng"}</Typography>;
      },
    }),
    TableHelper.createCell({
      field: "startDate",
      headerName: I18nTranslator(translations.masterData.useDate),
      size: "small",
      align: "left",
      sortable: true,
      renderCell: ({ value }) => {
        return (
          <Typography>{value ? TimeHelper.formatDate(value) : ""}</Typography>
        );
      },
    }),
    TableHelper.createCell({
      field: "expiryDate",
      headerName: I18nTranslator(translations.masterData.expiredDate),
      size: "small",
      align: "left",
      renderCell: ({ value }) => {
        return (
          <Typography>{TimeHelper.formatDate(value, "DD/MM/YYYY")}</Typography>
        );
      },
    }),
    TableHelper.createCell({
      field: "updatedDateTime",
      headerName: I18nTranslator(translations.masterData.updatedDateTime),
      size: "small",
      align: "left",
      renderCell: ({ value }) => {
        return (
          <Typography>
            {TimeHelper.formatDate(value, "DD/MM/YYYY HH:mm:ss")}
          </Typography>
        );
      },
    }),

    TableHelper.createCell({
      field: "userFullName",
      headerName: I18nTranslator(translations.masterData.lastActionUser),
      size: "medium",
      align: "left",
    }),

    TableHelper.createCell({
      field: "strLocationPutDate",
      headerName: I18nTranslator(translations.masterData.locationPutDate),
      size: "small",
      align: "left",
      renderCell: ({ value }) => {
        return (
          <Typography>
            {value ? TimeHelper.formatDate(value, "DD/MM/YYYY HH:ss:mm") : ""}
          </Typography>
        );
      },
    }),
    TableHelper.createCell({
      field: "strDriver",
      headerName: I18nTranslator(translations.masterData.forklift),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "strWarehouseVerifyDate",
      headerName: I18nTranslator(translations.masterData.warehouseVerifyDate),
      size: "small",
      align: "left",
      renderCell: ({ value }) => {
        return (
          <Typography>
            {value ? TimeHelper.formatDate(value, "DD/MM/YYYY HH:ss:mm") : ""}
          </Typography>
        );
      },
    }),
    TableHelper.createCell({
      field: "strWareHouseKeeper",
      headerName: I18nTranslator(translations.masterData.warehouseKeeper),
      size: "medium",
      align: "left",
    }),
    TableHelper.createCell({
      field: "printDateTime",
      headerName: I18nTranslator(translations.masterData.labelPrinterDate),
      size: "small",
      align: "left",
      sortable: true,
      renderCell: ({ value }) => (
        <Typography>
          {value ? moment(value).format("DD/MM/YYYY") : ""}
        </Typography>
      ),
    }),

    TableHelper.createCell({
      field: "printedTime",
      headerName: I18nTranslator(translations.masterData.printTime),
      size: "small",
      align: "center",
    }),

    TableHelper.createCell({
      field: "userPrintFullName",
      headerName: I18nTranslator(translations.masterData.printer),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "locationCode",
      headerName: I18nTranslator(translations.masterData.locationCode),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "createdDateTime",
      headerName: I18nTranslator(translations.masterData.createdDateTime),
      size: "small",
      align: "left",
      renderCell: ({ value }) => (
        <Typography>
          {value ? moment(value).format("DD/MM/YYYY HH:mm") : ""}
        </Typography>
      ),
    }),
  ];

  return (
    <Fragment>
      <AppTable<IPallet>
        title={I18nTranslator(translations.routes.palletConfiguration)}
        loading={loading}
        data={result}
        columns={COLUMNS}
        onAddNewClick={() => setAddPalletModalVisible(true)}
        onSearch={handleSearchTextChange}
        searchPlaceholder={I18nTranslator(
          translations.placeholder.searchPallet
        )}
        onChangePage={handlePageChange}
        removeActionColumn
        hideImport
        hideFilter={false}
        onPressFilter={handleFilterClick}
        onPageSizeChange={_handlePageSizeChange}
        onDownloadClick={_downloadPalletMasterData}
        isDownloading={downloading}
      />
      <ModalAddPallet
        open={addPalletModalVisible}
        isAdd={true}
        handleClose={() => setAddPalletModalVisible(false)}
      />
      <FilterPopup
        currentType={currentPalletType}
        anchor={anchorEl}
        onClose={() => setAnchorEl(null)}
        onSave={_handleTypeChange}
      />
    </Fragment>
  );
};

export default PalletConfiguration;
